import {
  BrandProductsRequest,
  SearchProductsRequest,
  SimilarProductsRequest,
  UserLikedProductsRequest,
} from '../domain/Product';
import { StaticPageType } from '../domain/StaticPage';
import { CategoriesRequest } from '../domain/Category';
import { PublicBlogsByCategoryRequest } from '../domain/Blog';
import { UserOrderItemsRequest } from './orderItems/queries';
import { ShippingRatesRequest } from '../domain/ShippingRate';

export const queryKeys = {
  categories: {
    topNavigation: ['categories', 'top-navigation'],
    categoryByName: (parentName: string, name: string) => [
      'categories',
      parentName,
      name,
    ],
    topNavigationChildren: (params: CategoriesRequest) => [
      'categories',
      'top-navigation-children',
      JSON.stringify(params),
    ],
    all: (params: CategoriesRequest) => [
      'categories',
      'public',
      'all',
      JSON.stringify(params),
    ],
    homepage: (params: CategoriesRequest) => [
      'categories',
      'homepage',
      JSON.stringify(params),
    ],
    children: (id?: number | string, params?: CategoriesRequest) => [
      'categories',
      id?.toString(),
      'children',
      JSON.stringify(params),
    ],
  },
  manufacturers: {
    main: ['manufacturers', 'main'],
  },
  brands: {
    options: ['brands', 'options'],
  },
  carousel: {
    get: ['carousel'],
  },
  products: {
    bySkuAndName: (name?: string, slug?: string, user?: string) => [
      'products',
      name,
      slug,
      user,
    ],
    bySlugAndVehicle: (slug?: string, vehicleId?: number, name?: string) => [
      'products',
      slug,
      vehicleId,
      name,
    ],
    bySlugBrandModel: (
      slug?: string,
      brand?: string,
      manufacturer?: string,
    ) => ['products', slug, manufacturer, 'brand', brand, 'model'],
    bySlugVehicles: (slug?: string, manufacturer?: string) => [
      'products',
      slug,
      manufacturer,
      'vehicles',
    ],
    bySlugCategory: (slug?: string, manufacturer?: string) => [
      'products',
      slug,
      manufacturer,
      'category',
    ],
    categoryTeaser: (categoryId?: number, user?: string) => [
      'products',
      'category-teaser',
      categoryId,
      user,
    ],
    search: (params: SearchProductsRequest) => [
      'products',
      'search',
      JSON.stringify(params),
    ],
    cartItems: (skus: string[], user?: string) => [
      'products',
      'cart',
      skus,
      user,
    ],
    likedProductsByUser: (
      params: UserLikedProductsRequest,
      likedProductIds: string[],
    ) => ['products', 'me', 'likedProducts', params, likedProductIds],
    likedProductsByUserIds: ['products', 'me', 'likedProductsIds'],
    featured: ['products', 'featured'],
    newest: ['products', 'newest'],
    similarProducts: (params: SimilarProductsRequest, user?: string) => [
      'similar',
      'products',
      params,
      user,
    ],
    brandProducts: (params: BrandProductsRequest) => [
      'brand',
      'products',
      params,
    ],
    top: ['products', 'top'],
  },
  vatRates: {
    public: ['vat-rates', 'public'],
  },
  vehicleModels: {
    byBrand: (brandId: number | null) => ['vehicleModels', brandId?.toString()],
  },
  vehicles: {
    byVehicleModel: (vehicleModelId: number | null) => [
      'vehicles',
      vehicleModelId?.toString(),
    ],
    bySelectedVehicle: (vehicleId: number | null) => [
      'vehicles',
      'selected',
      vehicleId?.toString(),
    ],
    userVehicles: ['vehicles', 'me'],
    byIds: (ids: number[]) => ['vehicles', [ids]],
  },
  blogs: {
    bySlug: (slug?: string) => ['blogs', slug],
    related: (slug?: string) => ['blogs', slug, 'related'],
    featured: ['blogs', 'featured'],
    newest: ['blogs', 'newest'],
    byCategorySlug: (params: PublicBlogsByCategoryRequest) => [
      'blogs',
      'category',
      params,
    ],
    recentlyRead: (ids: number[]) => ['blogs', 'recently-read', ids],
  },
  articleCategories: {
    get: ['articleCategories', 'initial'],
    getAllPublic: (blogId?: number | string) => [
      'articleCategories',
      'initial',
      'public',
      'all',
      blogId,
    ],
    getRemaining: (showAll?: boolean) => ['articleCategories', 'all', showAll],
    bySlug: (slug?: string) => ['articleCategory', slug],
  },
  staticPages: {
    byName: (name: StaticPageType) => ['static-pages', 'name', name],
    all: ['static-pages', 'all'],
  },
  bankDetails: {
    currentUser: ['bank-details', 'me'],
  },
  address: {
    currentUser: ['address', 'me'],
  },
  orderItems: {
    currentUser: (params: UserOrderItemsRequest) => [
      'order-items',
      'me',
      params,
    ],
  },
  faq: {
    all: ['faq', 'all'],
  },
  languages: {
    options: ['languages', 'options'],
  },
  promoCodes: {
    get: (code: string) => ['promoCode', code],
  },
  deliveryPrices: {
    all: ['deliveryPrices'],
  },
  shippingRates: {
    calculate: (params: ShippingRatesRequest) => [
      'shippingRates',
      'calculate',
      JSON.stringify(params),
    ],
  },
};
